import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

// StrictMode-safe Google reCAPTCHA v2 ("I'm not a robot" checkbox).
//
// The react-google-recaptcha package registers a `window.onloadcallback`
// global that React 18 StrictMode's dev double-mount tears down before
// Google's script fires it, causing:
//   "reCAPTCHA couldn't find user-provided function: onloadcallback".
// This component loads Google's api.js once (module-level singleton) and
// renders the widget explicitly, guarding against StrictMode double renders.

let scriptPromise = null;

function loadRecaptcha() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("reCAPTCHA requires a browser"));
  }
  if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
    return Promise.resolve(window.grecaptcha);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const callbackName = "__onRecaptchaApiLoad";
    window[callbackName] = () => resolve(window.grecaptcha);

    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=explicit&onload=" +
      callbackName;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

const Recaptcha = forwardRef(function Recaptcha(
  { sitekey, onChange, onExpired, onErrored },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !containerRef.current) return;
        // Guard against StrictMode double-invoke rendering twice into the
        // same node (grecaptcha.render throws if the element already has one).
        if (
          widgetIdRef.current !== null ||
          containerRef.current.childNodes.length > 0
        ) {
          return;
        }
        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey,
          callback: (token) => onChange && onChange(token),
          "expired-callback": () => onExpired && onExpired(),
          "error-callback": () => onErrored && onErrored(),
        });
      })
      .catch(() => {
        if (onErrored) onErrored();
      });

    return () => {
      cancelled = true;
    };
    // Only re-run if the site key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey]);

  return <div ref={containerRef} />;
});

export default Recaptcha;
